import React, { useState, useEffect } from 'react';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaSpinner,
} from 'react-icons/fa';
import {
  getDailyAlerts,
  addDailyAlert,
  updateDailyAlert,
  deleteDailyAlert,
  getCpvSuggestions,
  getKeywordSuggestions,
} from '../../services/accountService';
import MultiSelectInput from '../../components/MultiSelectInput/MultiSelectInput';
import { useToastMessages } from '../../components/MessageToast/MessageToast';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

const DailyAlerts = ({ userData, setUserData }) => {
  const [alerts, setAlerts] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cpvSuggestions, setCpvSuggestions] = useState([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [newAlert, setNewAlert] = useState({
    cpvCodes: [],
    keywords: [],
    enabled: true,
  });

  // Dialog state
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmă',
    cancelText: 'Anulează',
  });

  const { showSuccess, showError, showInfo, ToastContainer } =
    useToastMessages();

  // Helper function to show confirmation dialog
  const showConfirmDialog = (
    title,
    message,
    onConfirm,
    type = 'warning',
    confirmText = 'Confirmă',
  ) => {
    setDialogConfig({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setDialogConfig((prev) => ({ ...prev, isOpen: false }));
        onConfirm();
      },
      confirmText,
      cancelText: 'Anulează',
    });
  };

  const closeDialog = () => {
    setDialogConfig((prev) => ({ ...prev, isOpen: false }));
  };

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadAlerts(),
        loadCpvSuggestions(),
        loadKeywordSuggestions(),
      ]);
    } catch (error) {
      showError('A apărut o eroare la încărcarea datelor.');
    } finally {
      setLoading(false);
    }
  };

  const loadCpvSuggestions = async () => {
    try {
      const response = await getCpvSuggestions();
      if (response.success) {
        setCpvSuggestions(response.suggestions);
        if (response.suggestions.length > 0) {
          showInfo(
            `${response.suggestions.length} coduri CPV disponibile pentru selecție.`,
            3000,
          );
        }
      } else {
        showError(response.message);
      }
    } catch (error) {
      console.error('Eroare la încărcarea sugestiilor CPV:', error);
      showError('Nu s-au putut încărca sugestiile pentru codurile CPV.');
    }
  };

  const loadKeywordSuggestions = async () => {
    try {
      const response = await getKeywordSuggestions();
      if (response.success) {
        setKeywordSuggestions(response.suggestions);
      } else {
        showError(response.message);
      }
    } catch (error) {
      console.error(
        'Eroare la încărcarea sugestiilor pentru cuvinte cheie:',
        error,
      );
      showError('Nu s-au putut încărca sugestiile pentru cuvintele cheie.');
    }
  };

  const loadAlerts = async () => {
    try {
      const response = await getDailyAlerts();
      if (response.success) {
        setAlerts(response.alerts);
        if (response.message && response.alerts.length > 0) {
          showInfo(response.message, 4000);
        }
      } else {
        showError(response.message);
      }
    } catch (error) {
      showError('A apărut o eroare la încărcarea alertelor.');
    }
  };

  const handleAddAlert = async () => {
    if (newAlert.cpvCodes.length === 0 && newAlert.keywords.length === 0) {
      showError(
        'Trebuie să selectezi cel puțin un cod CPV sau un cuvânt cheie pentru a crea o alertă utilă.',
      );
      return;
    }

    // Procesarea codurilor CPV - extrage doar partea de cod (înainte de virgulă)
    var processedCpvCodes =
      newAlert.cpvCodes?.map((code) => {
        if (code.includes(',')) {
          return code.split(',')[0].trim(); // Extrage doar codul înainte de virgulă
        }
        return code.trim();
      }) ?? [];

    const alertToAdd = {
      ...newAlert,
      cpvCodes: processedCpvCodes,
    };

    setLoading(true);
    try {
      const response = await addDailyAlert(alertToAdd);
      if (response.success) {
        await loadAlerts(); // Reîncarcă lista
        setNewAlert({ cpvCodes: [], keywords: [], isActive: true });
        setIsAdding(false);
        showSuccess(response.message, 6000);
      } else {
        showError(response.message);
      }
    } catch (error) {
      showError('A apărut o eroare neașteptată la adăugarea alertei.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditAlert = async (id, updatedAlert) => {
    setLoading(true);
    try {
      const response = await updateDailyAlert(id, updatedAlert);
      if (response.success) {
        await loadAlerts(); // Reîncarcă lista
        setEditingId(null);
        showSuccess(response.message, 6000);
      } else {
        showError(response.message);
      }
    } catch (error) {
      showError('A apărut o eroare neașteptată la actualizarea alertei.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (id) => {
    // Găsește alerta pentru a afișa informații în confirmarea de ștergere
    const alertToDelete = alerts.find((a) => a.id === id);

    let confirmTitle = 'Confirmare ștergere alertă';
    let confirmMessage;

    if (alertToDelete) {
      const cpvCount = alertToDelete.cpvCodes?.length || 0;
      const keywordCount = alertToDelete.keywords?.length || 0;
      const statusText =
        alertToDelete.enabled || alertToDelete.isActive ? 'activă' : 'inactivă';

      confirmMessage =
        `Sunteți absolut sigur că doriți să ștergeți această alertă?\n\n` +
        `📊 Detalii alertă:\n` +
        `• ID: #${id}\n` +
        `• ${cpvCount} cod(uri) CPV monitorizat(e)\n` +
        `• ${keywordCount} cuvânt(e) cheie monitorizat(e)\n` +
        `• Status: ${statusText}\n\n` +
        `⚠️ ATENȚIE: Această acțiune este IREVERSIBILĂ!\n` +
        `Nu veți mai primi notificări pentru această configurație.`;
    } else {
      confirmMessage =
        `Sunteți sigur că doriți să ștergeți alerta #${id}?\n\n` +
        `⚠️ Această acțiune nu poate fi anulată.`;
    }

    showConfirmDialog(
      confirmTitle,
      confirmMessage,
      async () => {
        setLoading(true);
        try {
          const response = await deleteDailyAlert(id);
          if (response.success) {
            await loadAlerts(); // Reîncarcă lista
            // Folosește mesajul elegant de la server
            showSuccess(
              response.message || `🗑️ Alerta #${id} a fost ștearsă cu succes!`,
              7000,
            );
          } else {
            showError(response.message || 'Nu s-a putut șterge alerta.');
          }
        } catch (error) {
          showError(
            'A apărut o eroare neașteptată la ștergerea alertei. Vă rugăm să încercați din nou.',
          );
        } finally {
          setLoading(false);
        }
      },
      'error',
      'Șterge Definitiv',
    );
  };

  const toggleAlertStatus = async (id) => {
    const alert = alerts.find((a) => a.id === id);
    if (!alert) {
      showError(`Alerta cu ID-ul #${id} nu a fost găsită.`);
      return;
    }

    const currentStatus = alert.enabled || alert.isActive;
    const newStatus = !currentStatus;
    const actionText = newStatus ? 'activarea' : 'dezactivarea';
    const statusText = newStatus ? 'activă' : 'inactivă';

    // Confirmă acțiunea pentru mai multă claritate
    const confirmMessage =
      `Sunteți sigur că doriți ${actionText} alertei #${id}?\n\n` +
      `Alerta va deveni ${statusText} și ${newStatus ? 'VA' : 'NU VA'} trimite notificări.`;

    showConfirmDialog(
      `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} alertă`,
      confirmMessage,
      () => {
        const updatedAlert = {
          ...alert,
          enabled: newStatus,
          isActive: newStatus, // Pentru compatibilitate cu backend-ul
        };

        handleEditAlert(id, updatedAlert);
      },
      'info',
      newStatus ? 'Activează' : 'Dezactivează',
    );
  };

  return (
    <div className="space-y-6">
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg flex items-center gap-3">
            <FaSpinner className="animate-spin text-indigo-500 text-xl" />
            <span>Se procesează...</span>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 sm:gap-0">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-800">
            Alerte Zilnice
          </h2>
          <p className="text-gray-600 mt-2 text-sm sm:text-base">
            Configurează alerte zilnice pentru licitații bazate pe coduri CPV și
            cuvinte cheie
          </p>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          disabled={loading}
          className="flex items-center gap-2 bg-indigo-500 text-white px-3 sm:px-4 py-2 rounded-lg hover:bg-indigo-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
        >
          <FaPlus /> Adaugă Alertă
        </button>
      </div>

      {/* Formular pentru adăugare alertă */}
      {isAdding && (
        <div className="bg-gray-50 p-4 sm:p-6 rounded-lg border">
          <h3 className="text-lg font-semibold mb-4">Adaugă Alertă Nouă</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
            <MultiSelectInput
              label="Coduri CPV"
              placeholder="Selectează sau scrie coduri CPV..."
              values={newAlert.cpvCodes}
              onChange={(cpvCodes) =>
                setNewAlert((prev) => ({ ...prev, cpvCodes }))
              }
              suggestions={cpvSuggestions}
              allowCustom={true}
            />

            <MultiSelectInput
              label="Cuvinte Cheie"
              placeholder="Selectează sau scrie cuvinte cheie..."
              values={newAlert.keywords}
              onChange={(keywords) =>
                setNewAlert((prev) => ({ ...prev, keywords }))
              }
              suggestions={keywordSuggestions}
              allowCustom={true}
            />
          </div>

          <div className="flex items-center gap-4 mt-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={newAlert.enabled}
                onChange={(e) =>
                  setNewAlert((prev) => ({
                    ...prev,
                    enabled: e.target.checked,
                  }))
                }
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-700">
                Alertă activă (primește notificări)
              </span>
            </label>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 mt-6">
            <button
              onClick={handleAddAlert}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaSave /> Salvează
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewAlert({ cpvCodes: [], keywords: [], enabled: true });
              }}
              disabled={loading}
              className="flex items-center justify-center gap-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaTimes /> Anulează
            </button>
          </div>
        </div>
      )}

      {/* Lista alertelor existente */}
      <div className="space-y-4">
        {alerts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <FaPlus className="mx-auto text-4xl text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nu ai alerte configurate
            </h3>
            <p className="text-gray-600">
              Adaugă prima ta alertă zilnică pentru a fi notificat despre
              licitații relevante
            </p>
          </div>
        ) : (
          alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              editingId={editingId}
              setEditingId={setEditingId}
              onEdit={handleEditAlert}
              onDelete={handleDeleteAlert}
              onToggleStatus={toggleAlertStatus}
              loading={loading}
              cpvSuggestions={cpvSuggestions}
              keywordSuggestions={keywordSuggestions}
            />
          ))
        )}
      </div>

      {/* Dialog pentru confirmare */}
      <ConfirmDialog
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        onConfirm={dialogConfig.onConfirm}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
      />

      {/* Toast Container pentru mesajele de succes/eroare */}
      <ToastContainer />
    </div>
  );
};

const AlertCard = ({
  alert,
  editingId,
  setEditingId,
  onEdit,
  onDelete,
  onToggleStatus,
  loading,
  cpvSuggestions,
  keywordSuggestions,
}) => {
  const [editData, setEditData] = useState({
    cpvCodes: alert.cpvCodes || [],
    keywords: alert.keywords || [],
    enabled: alert.enabled !== undefined ? alert.enabled : alert.isActive,
  });

  // Dialog state for this card
  const [dialogConfig, setDialogConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    onConfirm: null,
    confirmText: 'Confirmă',
    cancelText: 'Anulează',
  });

  const { showSuccess, showError, showInfo, showWarning } = useToastMessages();

  const showConfirmDialog = (
    title,
    message,
    onConfirm,
    type = 'warning',
    confirmText = 'Confirmă',
  ) => {
    setDialogConfig({
      isOpen: true,
      title,
      message,
      type,
      onConfirm: () => {
        setDialogConfig((prev) => ({ ...prev, isOpen: false }));
        onConfirm();
      },
      confirmText,
      cancelText: 'Anulează',
    });
  };

  const closeDialog = () => {
    setDialogConfig((prev) => ({ ...prev, isOpen: false }));
  };

  const showToast = (message, type = 'info') => {
    switch (type) {
      case 'success':
        showSuccess(message);
        break;
      case 'error':
        showError(message);
        break;
      case 'warning':
        showWarning(message);
        break;
      default:
        showInfo(message);
    }
  };

  const isEditing = editingId === alert.id;
  const alertEnabled =
    alert.enabled !== undefined ? alert.enabled : alert.isActive;

  const handleSaveEdit = () => {
    // Validare înainte de salvare
    if (editData.cpvCodes.length === 0 && editData.keywords.length === 0) {
      showToast(
        '⚠️ Trebuie să aveți cel puțin un cod CPV sau un cuvânt cheie pentru a salva alerta.',
        'warning',
      );
      return;
    }

    // Confirmă modificările pentru claritate
    const changesCount =
      (JSON.stringify(editData.cpvCodes) !==
      JSON.stringify(alert.cpvCodes || [])
        ? 1
        : 0) +
      (JSON.stringify(editData.keywords) !==
      JSON.stringify(alert.keywords || [])
        ? 1
        : 0) +
      (editData.enabled !== alertEnabled ? 1 : 0);

    if (changesCount === 0) {
      showToast('ℹ️ Nu au fost detectate modificări pentru salvare.', 'info');
      return;
    }

    const confirmMessage =
      `Confirmați salvarea modificărilor pentru alerta #${alert.id}?\n\n` +
      `📊 Modificări detectate: ${changesCount}\n` +
      `• ${editData.cpvCodes.length} cod(uri) CPV\n` +
      `• ${editData.keywords.length} cuvânt(e) cheie\n` +
      `• Status: ${editData.enabled ? 'activă' : 'inactivă'}`;

    showConfirmDialog(
      'Confirmă modificările',
      confirmMessage,
      () => {
        onEdit(alert.id, {
          ...editData,
          isActive: editData.enabled, // Pentru compatibilitate cu backend-ul
        });
      },
      'success',
      'Salvează',
    );
  };

  const handleCancelEdit = () => {
    // Confirmă anularea dacă sunt modificări nesalvate
    const hasChanges =
      JSON.stringify(editData.cpvCodes) !==
        JSON.stringify(alert.cpvCodes || []) ||
      JSON.stringify(editData.keywords) !==
        JSON.stringify(alert.keywords || []) ||
      editData.enabled !== alertEnabled;

    if (hasChanges) {
      showConfirmDialog(
        'Anulare modificări',
        'Aveți modificări nesalvate. Sunteți sigur că doriți să anulați editarea?',
        () => {
          setEditData({
            cpvCodes: alert.cpvCodes || [],
            keywords: alert.keywords || [],
            enabled: alertEnabled,
          });
          setEditingId(null);
        },
        'warning',
        'Da, anulează',
      );
    } else {
      setEditData({
        cpvCodes: alert.cpvCodes || [],
        keywords: alert.keywords || [],
        enabled: alertEnabled,
      });
      setEditingId(null);
    }
  };

  return (
    <div
      className={`border rounded-lg p-4 sm:p-6 transition-all ${alertEnabled ? 'bg-white border-gray-200 shadow-sm' : 'bg-gray-50 border-gray-300'}`}
    >
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
        <div className="flex-1">
          {isEditing ? (
            <div className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <MultiSelectInput
                  label="Coduri CPV"
                  placeholder="Selectează sau scrie coduri CPV..."
                  values={editData.cpvCodes}
                  onChange={(cpvCodes) =>
                    setEditData((prev) => ({ ...prev, cpvCodes }))
                  }
                  suggestions={cpvSuggestions}
                  allowCustom={true}
                />

                <MultiSelectInput
                  label="Cuvinte Cheie"
                  placeholder="Selectează sau scrie cuvinte cheie..."
                  values={editData.keywords}
                  onChange={(keywords) =>
                    setEditData((prev) => ({ ...prev, keywords }))
                  }
                  suggestions={keywordSuggestions}
                  allowCustom={true}
                />
              </div>

              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editData.enabled}
                  onChange={(e) =>
                    setEditData((prev) => ({
                      ...prev,
                      enabled: e.target.checked,
                    }))
                  }
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">
                  Alertă activă (primește notificări)
                </span>
              </label>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-4">
                <div
                  className={`w-3 h-3 rounded-full ${alertEnabled ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}
                  title={
                    alertEnabled
                      ? 'Alerta este activă și trimite notificări'
                      : 'Alerta este dezactivată'
                  }
                ></div>
                <span
                  className={`font-semibold text-lg ${alertEnabled ? 'text-gray-900' : 'text-gray-500'}`}
                >
                  {alertEnabled ? '🔔 Activă' : '🔕 Inactivă'}
                </span>
                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                  ID: #{alert.id}
                </span>
              </div>

              {alert.cpvCodes && alert.cpvCodes.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 font-medium">
                    Coduri CPV:{' '}
                  </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {alert.cpvCodes.map((code, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                      >
                        {code}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {alert.keywords && alert.keywords.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 font-medium">
                    Cuvinte cheie:{' '}
                  </span>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {alert.keywords.map((keyword, index) => (
                      <span
                        key={index}
                        className="inline-block px-2 py-1 bg-green-100 text-green-800 rounded text-sm"
                      >
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {alert.createdAt && (
                <div className="text-sm text-gray-500">
                  Creat:{' '}
                  {new Date(alert.createdAt).toLocaleDateString('ro-RO', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2 lg:gap-2 lg:ml-6">
          {isEditing ? (
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <button
                onClick={handleSaveEdit}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-3 py-2 text-white bg-green-600 hover:bg-green-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                title="Salvează modificările"
              >
                <FaSave className="text-sm" />
                <span className="text-xs font-medium">Salvează</span>
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-3 py-2 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title="Anulează editarea"
              >
                <FaTimes className="text-sm" />
                <span className="text-xs font-medium">Anulează</span>
              </button>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
              <button
                onClick={() => onToggleStatus(alert.id)}
                disabled={loading}
                className={`flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
                  alertEnabled
                    ? 'text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200'
                    : 'text-green-600 bg-green-50 hover:bg-green-100 border border-green-200'
                }`}
                title={
                  alertEnabled
                    ? 'Dezactivează alerta (oprește notificările)'
                    : 'Activează alerta (pornește notificările)'
                }
              >
                <span className="text-lg">{alertEnabled ? '⏸️' : '▶️'}</span>
                <span className="text-xs font-medium">
                  {alertEnabled ? 'Dezactivează' : 'Activează'}
                </span>
              </button>

              <button
                onClick={() => {
                  setEditingId(alert.id);
                  setEditData({
                    cpvCodes: alert.cpvCodes || [],
                    keywords: alert.keywords || [],
                    enabled: alertEnabled,
                  });
                }}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-3 py-2 text-blue-600 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                title="Editează configurația alertei"
              >
                <FaEdit className="text-sm" />
                <span className="text-xs font-medium">Editează</span>
              </button>

              <button
                onClick={() => onDelete(alert.id)}
                disabled={loading}
                className="flex items-center justify-center gap-2 px-3 py-2 text-red-600 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                title="Șterge permanent această alertă"
              >
                <FaTrash className="text-sm" />
                <span className="text-xs font-medium">Șterge</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Dialog pentru confirmare */}
      <ConfirmDialog
        isOpen={dialogConfig.isOpen}
        onClose={closeDialog}
        onConfirm={dialogConfig.onConfirm}
        title={dialogConfig.title}
        message={dialogConfig.message}
        type={dialogConfig.type}
        confirmText={dialogConfig.confirmText}
        cancelText={dialogConfig.cancelText}
      />
    </div>
  );
};

export default DailyAlerts;
